import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import { ExternalLink } from "lucide-react";
import headshot from "@/assets/alicia-batts-headshot.jpg.asset.json";

const About = () => (
  <Layout>
    <PageHead
      title="About Alicia J. Batts"
      description="Founding partner of Batts Legal LLP, a Washington D.C. antitrust boutique. Former FTC Attorney Advisor with 30+ years in merger review and litigation."
      path="/about"
      keywords="Alicia Batts, Alicia J. Batts attorney, antitrust lawyer biography, FTC attorney advisor, competition law expert, Columbia Law School, Harvard, Washington DC antitrust firm"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Alicia J. Batts",
        jobTitle: "Founding Partner",
        worksFor: { "@type": "LegalService", name: "Batts Legal LLP", url: "https://www.battslegal.com" },
        alumniOf: [
          { "@type": "CollegeOrUniversity", name: "Columbia Law School" },
          { "@type": "CollegeOrUniversity", name: "Harvard College" },
        ],
        knowsAbout: ["Antitrust Law", "Competition Policy", "Merger Review", "FTC Investigations", "Antitrust Litigation", "DOJ Antitrust"],
        description: "Alicia J. Batts is a distinguished antitrust attorney and founding partner of Batts Legal LLP with over 30 years of experience in competition law, formerly an Attorney Advisor at the Federal Trade Commission.",
        url: "https://www.battslegal.com/about",
        address: { "@type": "PostalAddress", addressLocality: "Washington", addressRegion: "DC", addressCountry: "US" },
      }}
    />

    {/* Hero */}
    <section className="bg-primary text-primary-foreground py-20 lg:py-28">
      <div className="editorial-container">
        <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-center">
          <div className="max-w-3xl">
            <p className="text-sm tracking-widest uppercase text-primary-foreground/60 mb-4">
              Founder & Managing Partner
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold mb-6">
              Alicia J. Batts, J.D.
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80 leading-relaxed">
              Over 30 years of antitrust counsel for corporations and industry associations
              navigating merger control, enforcement, and complex competition disputes.
            </p>
          </div>
          <img
            src={headshot.url}
            alt="Alicia J. Batts, J.D., Founder & Managing Partner of Batts Legal LLP"
            className="w-56 h-56 lg:w-72 lg:h-72 rounded-sm object-cover border border-primary-foreground/20 shadow-xl"
            loading="eager"
          />
        </div>
      </div>
    </section>

    {/* Bio + Credentials */}
    <section className="py-16 lg:py-24 bg-background">
      <div className="editorial-container">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Bio */}
          <div className="space-y-5 text-muted-foreground leading-relaxed text-base md:text-lg">
            <p>
              Alicia is the founding partner of Batts Legal LLP, a boutique competition‑law firm
              in Washington, D.C. Her practice spans merger control, agency investigations, class
              actions, unfair competition, and compliance counseling before federal and state courts.
            </p>
            <p>
              A former Attorney Advisor at the Federal Trade Commission, Alicia brings deep
              government‑side insight to every engagement — informing the strategic approach that
              has earned her recognition across the antitrust bar.
            </p>
          </div>

          {/* Education */}
          <div className="flex flex-wrap gap-x-10 gap-y-3 text-foreground font-medium border-t border-border pt-8">
            <div>
              <span className="text-muted-foreground text-sm block">J.D.</span>
              Columbia Law School — Managing Editor, Columbia Business Law Review
            </div>
            <div>
              <span className="text-muted-foreground text-sm block">A.B.</span>
              Harvard College
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Representative Clients */}
    <section className="py-16 lg:py-24 bg-card border-y border-border">
      <div className="editorial-container">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm tracking-widest uppercase text-muted-foreground/60 mb-3">
            Select Experience
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-10">
            Representative Matters
          </h2>
          <div className="grid gap-4">
            {[
              { sector: "Pharmaceuticals", desc: "Antitrust investigations and merger reviews before the FTC" },
              { sector: "Financial Services", desc: "Competitor litigation and regulatory investigations for global institutions" },
              { sector: "Healthcare", desc: "Class actions alleging anticompetitive conduct on behalf of systems and hospital associations" },
              { sector: "Trade Associations", desc: "Advocacy before federal regulators and competition authorities" },
              { sector: "Technology", desc: "Monopolization and unfair competition litigation for platform companies" },
            ].map(({ sector, desc }) => (
              <div key={sector} className="flex items-start gap-5 p-5 rounded-lg border border-border bg-background hover:border-accent/40 transition-colors">
                <span className="shrink-0 w-2 h-2 rounded-full bg-accent mt-2.5" />
                <div>
                  <h3 className="font-serif text-base font-semibold text-foreground mb-1">{sector}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-muted-foreground/60 italic border-t border-border pt-6">
            Representative matters are provided for illustrative purposes only. Many engagements are confidential.
          </p>
        </div>
      </div>
    </section>

    {/* Selected Engagements & Recognition */}
    <section className="py-16 lg:py-24 bg-background border-b border-border">
      <div className="editorial-container">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-10">
            Selected Work & Recognition
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <InfoCard
              title="Trade Group Advocacy"
              text="Served as outside antitrust counsel to the Council of Multiple Listing Services (CMLS) on federal antitrust engagement; conference speaker on competition policy."
            />
            <InfoCard
              title="Law360 & GCR Author"
              text="Published analysis on evolving enforcement trends, pharmaceutical antitrust scrutiny, and U.S. monopolization law."
            />
            <InfoCard
              title="GCR Women in Antitrust, 2021"
              text="Honored among leading women making standout contributions to competition law globally."
            />
            <InfoCard
              title="Black Enterprise Top Lawyers"
              text="Recognized for excellence in legal service and impact."
            />
          </div>
        </div>
      </div>
    </section>

    {/* Publications & Press */}
    <section className="py-16 lg:py-24 bg-card border-b border-border">
      <div className="editorial-container">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm tracking-widest uppercase text-muted-foreground/60 mb-3">
            Thought Leadership
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-10">
            Publications & Press Coverage
          </h2>
          <div className="grid gap-4">
            <PressCard
              title="Consent Decrees Under the Biden Administration"
              outlet="PYMNTS / Competition Policy International"
              description="Analysis of the impact of Biden Administration competition policies on FTC and DOJ enforcement actions and consent decree practices."
              url="https://www.pymnts.com/cpi-posts/consent-decrees-under-the-biden-administration/"
            />
            <PressCard
              title="Seeking Clout, MLS Trade Group Taps Ex-DOJ and FTC Antitrust Lawyers"
              outlet="Inman News"
              description="Coverage of the Council of Multiple Listing Services retaining outside antitrust counsel, including Alicia Batts, to advise on competition policy and enforcement matters."
              url="https://www.inman.com/2022/10/06/seeking-clout-mls-trade-group-taps-ex-doj-and-ftc-antitrust-lawyers/"
            />
            <PressCard
              title="US Monopolisation"
              outlet="Global Competition Review — The Antitrust Review of the Americas (2012)"
              description="Chapter contribution analyzing U.S. monopolization law and enforcement trends for GCR's annual Americas review."
              url="https://globalcompetitionreview.com/review/the-antitrust-review-of-the-americas-archived/the-antitrust-review-of-the-americas-2012/article/us-monopolisation"
            />
            <PressCard
              title="US Monopolisation"
              outlet="Global Competition Review — The Antitrust Review of the Americas (2011)"
              description="Published analysis of developments in U.S. monopolization doctrine and practice."
              url="https://globalcompetitionreview.com/review/the-antitrust-review-of-the-americas-archived/the-antitrust-review-of-the-americas-2011/article/us-monopolisation"
            />
            <PressCard
              title="Recovery in the U.S. for Price-Fixing Abroad: The Future of FTAIA Litigation"
              outlet="PYMNTS / Competition Policy International (2011)"
              description="Co-authored analysis of evolving judicial interpretation of the Foreign Trade Antitrust Improvements Act and prospects for U.S. recovery in international cartel cases."
              url="https://www.pymnts.com/cpi-posts/recovery-in-the-u-s-for-price-fixing-abroad-the-future-of-ftaia-litigation/"
            />
            <PressCard
              title="Innovation, Antitrust And The Trump Administration"
              outlet="Investor's Business Daily"
              description="Commentary examining the intersection of innovation policy and antitrust enforcement under the Trump Administration."
              url="https://www.investors.com/politics/commentary/innovation-antitrust-and-the-trump-administration/"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Leadership & Connect */}
    <section className="py-16 lg:py-24 bg-background">
      <div className="editorial-container">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-6">Leadership</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>Former ABA Antitrust Section leadership</li>
              <li>Board service advancing justice and equity in the legal profession</li>
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-6">Connect</h2>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.linkedin.com/in/aliciabatts/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-foreground hover:text-accent transition-colors font-medium"
                >
                  LinkedIn <ExternalLink className="w-4 h-4" />
                </a>
              </li>
              <li className="text-muted-foreground">
                <span className="font-medium text-foreground">Press:</span> Law360, Inman News, Global Competition Review
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </Layout>
);

const InfoCard = ({ title, text }: { title: string; text: string }) => (
  <div className="bg-background rounded-lg p-6 border border-border">
    <h3 className="font-serif text-lg font-semibold text-foreground mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
  </div>
);

const PressCard = ({ title, outlet, description, url }: { title: string; outlet: string; description: string; url: string }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-start gap-5 p-5 rounded-lg border border-border bg-background hover:border-accent/40 transition-colors group"
  >
    <span className="shrink-0 w-2 h-2 rounded-full bg-accent mt-2.5" />
    <div>
      <h3 className="font-serif text-base font-semibold text-foreground mb-1 group-hover:text-accent transition-colors">
        {title}
      </h3>
      <p className="text-xs font-medium text-accent/80 mb-1">{outlet}</p>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
    <ExternalLink className="w-4 h-4 shrink-0 text-muted-foreground/40 group-hover:text-accent transition-colors mt-1 ml-auto" />
  </a>
);

export default About;
